import { defineStore } from 'pinia';
import { type UserTypes, Constants, Species } from '@fibl/oxs-common';
import type { EAV } from '@fibl/zdb-common';
import debounce from 'debounce';
import * as SearchRepo from '@/repository/search';
import { toLocale } from '@/util/locale-helper';
import { createDeferred, type Deferred } from '@/util/flow-helper';
import { speciesLocale } from '@/util/locale-helper';

const maxEntitiesPerCall = Constants.MAX_ENTITY_PER_CALL;


const debouncedLoadEntities = debounce(async function load(
	that: ReturnType<typeof useRelatedStore>,
) {
	let wanted = that.relWanted;
	if (wanted.length === 0) return;
	let searchIds = wanted.slice(0, maxEntitiesPerCall);
	let restIds = wanted.slice(maxEntitiesPerCall);
	that.clearWanted(restIds);
	let found = await SearchRepo.getEntitiesById(searchIds);
	let unavailable = searchIds.filter((id) => {
		return !found.find((entity) => entity.id === id);
	});
	that.addRelEntity(found);
	that.addUnavailable(unavailable);
	if (that.relWanted.length > 0) {
		debouncedLoadEntities(that);
	}
},
100);

type Ent = EAV.Entity<Record<string, unknown>>;

export const useRelatedStore = defineStore('related', {
	state: () => ({
		relWanted: [] as string[],
		relUnavailable: [] as string[],
		relEntities: [] as Ent[],
		openPromises: {} as Record<string, Deferred<Ent>>,
		waitingForLoaded: [] as Deferred<void>[],
	}),
	getters: {
		byId() {
			const idx = new Map(Object.values(this.relEntities).map((e) => [e.id, e]));
			return (id: string) => {
				return idx.get(id);
			};
		},
	},
	actions: {
		addEntity(id: string): Promise<Ent> {
			if (!id) throw new Error('no id given');
			let existing = this.byId(id);
			if (existing) return Promise.resolve(existing);
			if (!(id in this.openPromises)) {
				let def = createDeferred<Ent>();
				this.openPromises[id] = def;
				this.addWanted(id);
				debouncedLoadEntities(this);
			}
			return this.openPromises[id].promise;
			// if (this.relWanted.includes(id) || this.relUnavailable.includes(id) || this.relEntities.find(e => e.id === id)) return;
		},
		addWanted(id: string) {
			// console.log('wantedOther', id, 'not in', this.relEntities.length);
			this.relWanted.push(id);
		},
		addRelEntity(entities: EAV.Entity<Record<string, unknown>>[]) {
			if (entities.length === 0) return;
			// console.log('addfound', entities.length)
			this.relEntities = this.relEntities.concat(entities);
			for (let ent of entities) {
				let key = ent.id;
				if (key in this.openPromises) {
					this.openPromises[key].resolve(ent);
				}
			}
			setTimeout(this._checkLoadingDone, 20);
		},
		_checkLoadingDone() {
			// console.debug('checkLoadingDone', {
			// 	wanted: this.relWanted.length
			// });
			if (this.relWanted.length === 0) {
				while (this.waitingForLoaded.length) {
					let def = this.waitingForLoaded.pop();
					def?.resolve();
				}
			}
		},
		waitForLoadingComplete() {
			if (this.relWanted.length === 0) {
				return Promise.resolve();
			}
			let def = createDeferred<void>();
			this.waitingForLoaded.push(def);
			return def.promise;
		},
		sideloadRelatedEntities(entities: EAV.Entity<Record<string, unknown>>[]) {
			//saving from other sources like speciesSelect or sortSelect
			for (let e of entities) {
				if (!this.relEntities.find((x) => x.id === e.id)) {
					this.relEntities.push(e);
				}
			}
		},
		addUnavailable(ids: string[]) {
			if (ids.length === 0) return;
			// console.log('adding unavailable', ids.length);
			this.relUnavailable = this.relUnavailable.concat(ids);
			for (let id of ids) {
				if (id in this.openPromises) {
					this.openPromises[id].reject(new Error('id not found: ' + id));
				}
			}
		},
		clearWanted(keepIds?: string[]) {
			if (keepIds && keepIds.length) {
				this.relWanted = keepIds;
			} else if (this.relWanted.length > 0) {
				this.relWanted = [];
			}
		},
		clearStale() {
			while (this.relEntities.length > 1000) {
				let e = this.relEntities.shift();
				let id = e!.id;
				delete this.openPromises[id];
			}
		},
		getLabel(id: string): string {
			if (!id) return '';
			let ent = this.byId(id);
			if (!ent) {
				this.addEntity(id);
				return '...';
			}
			if ('labels' in ent.data) {
				return toLocale(ent.data.labels);
			}
			if ('publicName' in ent.data) {
				return toLocale(ent.data.publicName);
			}
			return '?';
		},
		getSpeciesLabel(id: string, botName?: boolean) {
			let ent = this.byId(id) as EAV.Entity<Species.Species>;
			if (!ent) {
				this.addEntity(id);
				return '...';
			}
			if (botName && 'botName' in ent.data) {
				return `${ent.data.botName}`;
			}
			if ('labels' in ent.data) {
				return `${speciesLocale(ent.data.labels)}`;
			}
			return '?';
		},
		getAccountName(id: string): string {
			if (!id) return '';
			const res = this.byId(id) as EAV.Entity<UserTypes.AnyAccount>;
			if (!res) {
				this.addEntity(id);
				return '...';
			}
			if (res && res.indexfields && 'name' in res.indexfields) {
				return res.indexfields.name as string;
			}
			return '?';
		},
		reset() {
			this.relWanted = [];
			this.relEntities = [];
			this.openPromises = {};
		},
	},
});
